import { ExtractComponentProp } from '@approvalmax/types';
import { PageWrapper, Popup, useLayout } from '@approvalmax/ui/src/components';
import debounce from 'lodash/debounce';
import { selectors } from 'modules/common';
import { getActiveCompany } from 'modules/common/selectors/navigationSelectors';
import { hocs as pageHocs } from 'modules/page/hocs';
import { useDispatch, useSelector } from 'modules/react-redux';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { amplitudeService } from 'services/amplitude';
import { HelpStripe } from 'shared/components';

import { pageConfig } from './config';
import Loading from './loading';
import { cancelActivePopup } from './resources/actions';
import {
    AddUsersPopupContent,
    InviteUsersPopupContent,
    OffboardUserPopupContent,
    SetDelegatePopupContent,
    UserList,
} from './resources/components';
import { Toolbar } from './resources/components/Toolbar/Toolbar';
import { createHelpItemId } from './resources/helpers/createHelpItemId';
import { useBreadcrumbs } from './resources/hooks/useBreadcrumbs';
import { usePageData } from './resources/hooks/usePageData';
import { messages } from './resources/messages';
import { PageActivePopup } from './resources/reducers/page/activePopupReducer';
import { getActivePopup } from './resources/selectors/pageSelectors';

const Content = memo(() => {
    const dispatch = useDispatch();
    const activePopup = useSelector(getActivePopup);
    const company = useSelector(getActiveCompany);
    const [effectiveFilterText, setEffectiveFilterText] = useState('');
    const [checkedItemIds, setCheckedItemIds] = useState<string[]>([]);

    const triggerThrottledFilterChange = useMemo(
        () =>
            debounce((value: string) => {
                setEffectiveFilterText(value);
            }, 250),
        []
    );

    useBreadcrumbs();
    useEffect(() => {
        amplitudeService.sendData('users: view list');
    }, []);

    const onOpenCallback = useCallback<ExtractComponentProp<typeof Popup, 'onToggle'>>(
        (open) => {
            !open && dispatch(cancelActivePopup());
        },
        [dispatch]
    );

    const helpItemId = createHelpItemId(company.integration?.integrationType);

    return (
        <PageWrapper>
            <Helmet title={messages.title({ companyName: company.displayName })} />

            <PageWrapper.Toolbar spacingBottom={0}>
                <HelpStripe id={helpItemId} spacingTop={0} />

                <Toolbar
                    triggerThrottledFilterChange={triggerThrottledFilterChange}
                    checkedItemIds={checkedItemIds}
                    setCheckedItemIds={setCheckedItemIds}
                    helpStripeId={helpItemId}
                />
            </PageWrapper.Toolbar>

            <PageWrapper.Content spacingTop={0}>
                <UserList
                    effectiveFilterText={effectiveFilterText}
                    checkedItemIds={checkedItemIds}
                    setCheckedItemIds={setCheckedItemIds}
                />
            </PageWrapper.Content>

            <Popup
                size='large'
                open={Boolean(activePopup && activePopup.type === PageActivePopup.AddUsersPopup)}
                closable
                onToggle={onOpenCallback}
            >
                <AddUsersPopupContent />
            </Popup>

            <Popup
                size='small'
                open={Boolean(activePopup && activePopup.type === PageActivePopup.InviteUsersPopup)}
                closable
                onToggle={onOpenCallback}
            >
                <InviteUsersPopupContent />
            </Popup>

            <Popup
                closable
                size='large'
                open={Boolean(activePopup && activePopup.type === PageActivePopup.SetDelegatePopup)}
                onToggle={onOpenCallback}
            >
                <SetDelegatePopupContent />
            </Popup>

            <Popup
                size='large'
                open={Boolean(activePopup && activePopup.type === PageActivePopup.OffboardUserPopup)}
                closable
                onToggle={onOpenCallback}
            >
                <OffboardUserPopupContent />
            </Popup>
        </PageWrapper>
    );
});

export const Page = memo(() => {
    const activeCompanyId = useSelector(selectors.navigation.getActiveCompanyId)!;

    useLayout({ hasHeader: true, hasDrawer: true });

    const { isInitialLoading } = usePageData();

    return isInitialLoading || !activeCompanyId ? <Loading /> : <Content />;
});

export default pageHocs.withProtected({ Page, roles: pageConfig.access });
